import { createPollingFailureEpisode, type RequestFailureReason } from "./requestFailure";

export interface PollTick {
  /** Whether the interval (rather than `run()`) started this poll. */
  fromInterval: boolean;
  /** Aborted by `stop()`; pass it to the request and check it before applying a result. */
  signal: AbortSignal;
}

export interface IntervalPollerOptions {
  /** One poll. It throws when the request fails. */
  tick: (poll: PollTick) => Promise<void>;
  intervalMs: number;
  /** What the poll is for: the reason of its breadcrumbs and degradations. */
  reason: RequestFailureReason;
  /** Logs an unexpected failure; called once per failure episode. */
  report?: (error: unknown) => void;
}

export interface IntervalPoller {
  /** Polls now; a success restarts an interval stopped by a hard failure. */
  run(): Promise<void>;
  /** Stops polling for good, aborts a poll in flight and ignores its outcome. */
  stop(): void;
}

/**
 * Polls `tick` every `intervalMs` without overlapping polls. A 401/403/404
 * stops the interval until a later `run()` (focus, visibility, refresh event)
 * succeeds; failures are reported once per failure episode instead of per tick,
 * a 5xx as a degradation (see reportRequestFailure).
 */
export function startIntervalPoller({
  tick,
  intervalMs,
  reason,
  report,
}: IntervalPollerOptions): IntervalPoller {
  const episode = createPollingFailureEpisode(reason, report);
  const controller = new AbortController();
  let timer: number | undefined;
  let stopped = false;
  let inFlight = false;

  const schedule = () => {
    if (timer === undefined && !stopped) {
      timer = window.setInterval(() => void poll(true), intervalMs);
    }
  };
  const halt = () => {
    window.clearInterval(timer);
    timer = undefined;
  };
  const poll = async (fromInterval: boolean) => {
    if (stopped || inFlight) return;
    inFlight = true;
    try {
      await tick({ fromInterval, signal: controller.signal });
      if (stopped) return;
      episode.succeed();
      schedule();
    } catch (error) {
      if (!stopped && episode.fail(error)) halt();
    } finally {
      inFlight = false;
    }
  };

  schedule();
  return {
    run: () => poll(false),
    stop() {
      stopped = true;
      halt();
      controller.abort();
    },
  };
}
